import { Component, Inject } from '@angular/core';
import { Validators } from '@angular/forms';
import { AppReference, FicheConseil, Investisseur } from '@lib/models/generated/graphql';
import { AuthService } from '@lib/services/auth-service.service';
import { QueryManagerService } from 'finzzle-angular-utils';
import { OuiButtonToggleOption } from 'omnium-ui/button-toggle';
import { OuiValidationError } from 'omnium-ui/form-field';
import { AbstractStepComponent } from 'src/app/components/declare-fiche-conseil/steps/abstract-step.component';

import { ConnaissanceClient } from 'src/app/services/ConnaissanceClient';
import { FicheConseilService } from 'src/app/services/fiche-conseil.service';
import { FormConfigService } from 'src/app/services/form-config.service';
import { FieldFormControl } from 'src/fieldFormControl/FieldFormControl';

@Component({
  selector: 'app-step-connaissance-client',
  templateUrl: './step-connaissance-client.component.html',
  styleUrls: ['./step-connaissance-client.component.scss'],
})
export class StepConnaissanceClientComponent extends AbstractStepComponent {
  toleranceOptions: OuiButtonToggleOption<boolean>[] = [
    { value: true, label: 'Oui' },
    { value: false, label: 'Non' },
  ];

  invToleranceControl: FieldFormControl<boolean, boolean>;
  coInvToleranceControl: FieldFormControl<boolean, boolean>;
  investisseurId: number;
  coInvestisseurId: number;
  isLoadingConnaisanceClient = false;
  readonly requiredValueError: OuiValidationError = {
    code: 'required',
    message: 'Ce champs est requis !',
  };
  onModifyClick(isCoInvestisseur: boolean) {
    const fiche = this.ficheConseilService?.currentFicheConseilSubject?.value;
    const inv = isCoInvestisseur ? fiche?.coInvestisseur : fiche?.investisseur;
    const cons = this.authService.currentConsultant;

    if (
      inv?.code &&
      (cons?.gerePar === AppReference.Minotaure || cons?.gerePar === AppReference.MinotaureWithWarning)
    ) {
      window.open(this.environment.proxeemaBaseUrl + '/investisseurs/from-list/' + inv.code, '_blank');
    }
  }

  isBlocked = false;
  environment: any;
  isFirstTry = true;
  constructor(
    ficheConseilService: FicheConseilService,
    private formConfigService: FormConfigService,
    private queryManager: QueryManagerService,
    protected authService: AuthService,
    @Inject('environment')
    environment: any
  ) {
    super(ficheConseilService);
    this.environment = environment;
  }

  override ngOnInit(): void {
    super.ngOnInit();

    if (!this.currentFicheConseil?.investisseur?.situationPersonnelle) {
      this.getConnaissanceClientData({
        investisseur: this.currentFicheConseil?.investisseur ?? undefined,
        coInvestisseur: this.currentFicheConseil?.coInvestisseur ?? undefined,
      }).then(() => {
        this.setControlTolerancePerte();
      });
    } else {
      this.setControlTolerancePerte();
    }
  }

  setControlTolerancePerte() {
    if (this.currentFicheConseil?.investisseur) {
      this.invToleranceControl = new FieldFormControl<boolean, boolean>({
        formConfigService: this.formConfigService,
        ficheConseilService: this.ficheConseilService,
        dataKey: 'investisseur.profil.hasTolerancePerte',
        ficheDataToFieldDataWrapper: (data: boolean | null) => {
          return !!data;
        },
        defaultValue: true,
        options: {
          validators: [Validators.required],
        },
      });
      if (
        this.currentFicheConseil?.investisseur?.profil?.hasTolerancePerte == null ||
        this.currentFicheConseil?.investisseur?.profil?.hasTolerancePerte == undefined
      ) {
        this.invToleranceControl.setValue(true);
      }
      if (this.currentFicheConseil?.coInvestisseur) {
        this.coInvToleranceControl = new FieldFormControl<boolean, boolean>({
          formConfigService: this.formConfigService,
          ficheConseilService: this.ficheConseilService,
          dataKey: 'coInvestisseur.profil.hasTolerancePerte',
          ficheDataToFieldDataWrapper: (data: boolean | null) => !!data,
          defaultValue: true,
          options: {
            validators: [Validators.required],
          },
        });
        if (
          this.currentFicheConseil?.coInvestisseur?.profil?.hasTolerancePerte == null ||
          this.currentFicheConseil?.coInvestisseur?.profil?.hasTolerancePerte == undefined
        ) {
          this.coInvToleranceControl.setValue(true);
        }
      }
    }
  }
  override beforeFicheConseilUpdate(
    newFicheConseil: FicheConseil | null,
    previousFicheConseil: FicheConseil | null
  ): void {
  }

  public override isStepValid(): boolean {
    let isValid =
      !!this.currentFicheConseil?.investisseur?.dateNaissance &&
      !!this.currentFicheConseil?.investisseur?.situationPersonnelle?.etatCivilTypeCode &&
      !!this.currentFicheConseil?.investisseur?.situationPersonnelle?.profession &&
      !!this.currentFicheConseil?.investisseur?.profil?.profilQpiCode &&
      this.invToleranceControl.valid;

    if (this.currentFicheConseil?.coInvestisseur) {
      isValid =
        isValid &&
        !!this.currentFicheConseil?.coInvestisseur?.dateNaissance &&
        !!this.currentFicheConseil?.coInvestisseur?.situationPersonnelle?.etatCivilTypeCode &&
        !!this.currentFicheConseil?.coInvestisseur?.situationPersonnelle?.profession &&
        !!this.currentFicheConseil?.coInvestisseur?.profil?.profilQpiCode &&
        this.coInvToleranceControl.valid;
    }

    return !!isValid;
  }

  public override markAsTouched(): void {
    if (this.currentFicheConseil?.coInvestisseur) {
      this.coInvToleranceControl?.markAsTouched();
    }
    this.invToleranceControl.markAsTouched();
    this.isBlocked = !this.isStepValid();
  }
  async getConnaissanceClientData(investisseurs?: { investisseur?: Investisseur; coInvestisseur?: Investisseur }) {
    this.isLoadingConnaisanceClient = true;
    this.isBlocked = false;
    let tempInvestisseurs = investisseurs;
    if (!tempInvestisseurs) {
      tempInvestisseurs = {
        investisseur: this.currentFicheConseil?.investisseur ?? undefined,
        coInvestisseur: this.currentFicheConseil?.coInvestisseur ?? undefined,
      };
    }
    const clients = await ConnaissanceClient.updateConnaissanceClientData(
      this.queryManager,
      tempInvestisseurs
    );
    if (clients) {
      this.ficheConseilService.editConnaisanceClient(clients);
    }
    this.isLoadingConnaisanceClient = false;
  }
}
